import { IExportColumnData, IExportRowData } from "api/interfaces";
import ContractModel from "api/models/ContractModel";
import { IExportImage } from "components/interfaces";
import { lang } from "i18nLang";
import ExcelService, {
  alignments,
  borders,
  createBorderStyle,
  ExcelWorksheet,
  fillColors,
  fonts,
  getColumnLetter,
} from "./ExcelService";

class ExportXLSXService {
  private readonly fmtNumeric = "#,##0.00;[Red]-#,##0.00";
  private readonly fmtMonetary = '"R$ "#,##0.00;[Red]-"R$ "#,##0.00';
  private readonly rowHeight = 17;
  private readonly bodyStartRow = 10;

  public createWorkbook() {
    return new ExcelService();
  }

  public createWorsheet(
    workbook: ExcelService,
    contract: ContractModel,
    title: string,
    columns: IExportColumnData[],
    rows: IExportRowData[],
    companyImage?: IExportImage,
    footerData?: IExportRowData
  ): ExcelService {
    const worksheet = workbook.createWorksheet(title);
    const columnsCount = this.getColumnsCount(columns);
    const lastColumn = getColumnLetter(columnsCount);

    this.setFixedRowsHeight(worksheet);
    this.setFixedColumnsWidth(worksheet, columns);
    this.createHeader(worksheet, contract, lastColumn, companyImage);
    this.createBlankRow(worksheet, lastColumn);

    this.createColumnsTitle(worksheet, columns);
    this.createBody(worksheet, columns, rows);

    if (footerData) {
      const footerIndex = rows.length + this.bodyStartRow + 1;
      this.createFooter(worksheet, columns, footerIndex, footerData);
    }

    return workbook;
  }

  private setFixedRowsHeight(worksheet: ExcelWorksheet) {
    for (let i = 1; i <= this.bodyStartRow; i++) {
      const height = i === 1 ? 10 : this.rowHeight;
      worksheet.setRowHeight(i, height);
    }
  }

  private setFixedColumnsWidth(
    worksheet: ExcelWorksheet,
    columns: IExportColumnData[]
  ) {
    worksheet.setColumnWidth(1, 1);

    let index = 2;
    columns.forEach((clm) => {
      const colspan = clm.colspan || 1;
      for (let i = 0; i < colspan; i++) {
        worksheet.setColumnWidth(index, clm.width || 10);
        index++;
      }
    });
  }

  private createHeader(
    worksheet: ExcelWorksheet,
    contract: ContractModel,
    lastColumn: string,
    companyImage?: IExportImage
  ) {
    this.mergeHeaderCells(worksheet, lastColumn);
    this.formatHeaderCellsBorder(worksheet);
    this.setHeaderDatasTitles(worksheet);
    this.setHeaderDatasValues(worksheet, contract);

    if (companyImage) {
      worksheet.addImage(companyImage, 1.5, 1.8);
    }
  }

  private mergeHeaderCells(worksheet: ExcelWorksheet, lastColumn: string) {
    worksheet.mergeCells(`B2:${lastColumn}2`);
    worksheet.mergeCells(`B3:C3`);
    worksheet.mergeCells(`E3:${lastColumn}3`);
    worksheet.mergeCells(`B4:C4`);
    worksheet.mergeCells(`E4:${lastColumn}4`);
    worksheet.mergeCells(`B5:C5`);
    worksheet.mergeCells(`E5:${lastColumn}5`);
    worksheet.mergeCells(`B6:C6`);
    worksheet.mergeCells(`E6:${lastColumn}6`);
    worksheet.mergeCells(`B7:${lastColumn}7`);
  }

  private formatHeaderCellsBorder(worksheet: ExcelWorksheet) {
    worksheet.cell("B2").format({
      border: {
        ...borders.allThick,
        bottom: createBorderStyle("thin", "FFFFFF"),
      },
    });
    ["B3", "B4", "B5", "B6"].forEach((cell) => {
      worksheet.cell(cell).format({
        border: {
          ...borders.allThinWhite,
          left: createBorderStyle("thick", "000000"),
        },
      });
    });
    worksheet.cell("B7").format({
      border: {
        ...borders.allThick,
        top: createBorderStyle("thin", "FFFFFF"),
      },
    });
  }

  private setHeaderDatasTitles(worksheet: ExcelWorksheet) {
    const style = {
      font: fonts.InfoTitle,
      border: borders.allThinWhite,
    };

    worksheet.cell(`D3`).set(lang(`exportData.project`), style);
    worksheet.cell(`D4`).set(lang(`exportData.contract`), style);
    worksheet.cell(`D5`).set(lang(`exportData.customer`), style);
    worksheet.cell(`D6`).set(lang(`exportData.location`), style);
  }

  private setHeaderDatasValues(
    worksheet: ExcelWorksheet,
    contract: ContractModel
  ) {
    const style = {
      font: fonts.Header,
      border: {
        ...borders.allThinWhite,
        right: createBorderStyle("thick", "000000"),
      },
    };

    worksheet.cell(`E3`).set(contract.development.name, style);
    worksheet.cell(`E4`).set(contract.name, style);
    worksheet.cell(`E5`).set(contract.development.customer, style);
    worksheet.cell(`E6`).set(contract.development.local, style);
  }

  private createBlankRow(worksheet: ExcelWorksheet, lastColumn: string) {
    worksheet.mergeCells(`B8:${lastColumn}9`);
    worksheet.cell("B8").format({
      border: borders.allThick,
    });
  }

  private createColumnsTitle(
    worksheet: ExcelWorksheet,
    columns: IExportColumnData[]
  ) {
    let data: IExportRowData = {};
    columns.forEach((clm) => {
      data[clm.dataKey] = clm.label;
    });

    this.fillRow(worksheet, columns, this.bodyStartRow, data, true);
  }

  private createBody(
    worksheet: ExcelWorksheet,
    columns: IExportColumnData[],
    rowDatas: IExportRowData[]
  ) {
    rowDatas.forEach((rowData, index) => {
      const rowIndex = index + this.bodyStartRow + 1;
      const lastRow = index === rowDatas.length - 1;
      this.setRowHeight(worksheet, rowIndex, this.rowHeight, rowData.rowspan);
      this.fillRow(
        worksheet,
        columns,
        rowIndex,
        rowData,
        false,
        false,
        lastRow
      );
    });
  }

  private createFooter(
    worksheet: ExcelWorksheet,
    columns: IExportColumnData[],
    rowIndex: number,
    footerData: IExportRowData
  ) {
    this.setRowHeight(worksheet, rowIndex, 20, footerData.rowspan);
    this.fillRow(worksheet, columns, rowIndex, footerData, false, true);
    this.setFooterFormulas(worksheet, columns, rowIndex, footerData);
  }

  private fillRow(
    worksheet: ExcelWorksheet,
    columns: IExportColumnData[],
    rowIndex: number,
    rowData: IExportRowData,
    isTitle = false,
    isFooter = false,
    lastRow = false
  ) {
    const columnsCount = this.getColumnsCount(columns);
    let colIndex = 1;
    columns.forEach((clm) => {
      const cellColumn = getColumnLetter(colIndex);
      const cellKey = `${cellColumn}${rowIndex}`;
      const style = {
        font: isTitle ? fonts.Title : fonts.Text,
        border: lastRow ? borders.thickBottom : borders.allThin,
        alignment: alignments.MiddleAndLeft,
      };

      if (clm.colspan || rowData.rowspan) {
        const lastLetter = clm.colspan
          ? getColumnLetter(colIndex + clm.colspan - 1)
          : cellColumn;
        const lastRowIndex = rowData.rowspan
          ? rowIndex + rowData.rowspan - 1
          : rowIndex;
        worksheet.mergeCells(`${cellKey}:${lastLetter}${lastRowIndex}`);
      }

      if (isFooter) {
        style.font = fonts.FooterValue;
        style.alignment = alignments.MiddleAndCenter;
        style.border = borders.thickTopBottom;
      }

      if (clm.type === "numeric") {
        style.alignment = isTitle
          ? alignments.MiddleAndCenter
          : alignments.MiddleAndRight;
        if (!isTitle) style["numFmt"] = this.fmtNumeric;
      }

      if (clm.type === "monetary") {
        style.alignment = isTitle
          ? alignments.MiddleAndCenter
          : alignments.MiddleAndRight;
        if (!isTitle) style["numFmt"] = this.fmtMonetary;
      }

      if (colIndex === 1) {
        style.border = {
          ...style.border,
          left: createBorderStyle("thick"),
        };
      }

      if (colIndex === columnsCount) {
        style.border = {
          ...style.border,
          right: createBorderStyle("thick"),
        };
      }

      if (rowIndex % 2 !== 0) {
        style["fill"] = fillColors.gray;
      }

      if (rowData[clm.dataKey] === "-") {
        style.alignment = alignments.MiddleAndCenter;
      }

      worksheet.cell(cellKey).set(rowData[clm.dataKey], style);
      colIndex += clm.colspan || 1;
    });
  }

  private setFooterFormulas(
    worksheet: ExcelWorksheet,
    columns: IExportColumnData[],
    rowIndex: number,
    footerData: IExportRowData
  ) {
    let colIndex = 1;
    columns.forEach((clm) => {
      const cellColumn = getColumnLetter(colIndex);
      const cellKey = `${cellColumn}${rowIndex}`;

      if (
        (clm.type === "numeric" || clm.type === "monetary") &&
        footerData[clm.dataKey]
      ) {
        const formula = `SUM(${cellColumn}11:${cellColumn}${rowIndex - 1})`;
        const result = footerData[clm.dataKey];
        worksheet.cell(cellKey).setFormula(formula, result);
      }

      colIndex += clm.colspan || 1;
    });
  }

  private setRowHeight(
    worksheet: ExcelWorksheet,
    rowIndex: number,
    height: number,
    rowspan: number = 1
  ) {
    for (let i = 0; i < rowspan; i++) {
      worksheet.setRowHeight(rowIndex + i, height);
    }
  }

  private getColumnsCount(columns: IExportColumnData[]) {
    return columns.reduce(
      (acc, clm) => acc + (clm.colspan ? clm.colspan : 1),
      0
    );
  }
}

export default new ExportXLSXService();
